import type { Job } from "@stryker/core/types/jobs";
import { booleanToYesNo } from "@/utils/mappings";
import { formatMaterialLabel } from "@/utils/materials";
import { formatAdditionalNotesFromList } from "@/utils/notes";

type AdditionalNotesLookup = {
  notes: Array<{ id: number; name: string }>;
};

const compositeCardTitles = [
  "Job Details",
  "Additional Job Details",
  "Contact Information",
  "Design Conditions",
  "Operating Conditions",
  "Component Details",
  "Defect Details",
  "Checks",
  "Slot Details",
  "Pinhole Details",
  "Diameter Encompassing Entire thin Defect Details",
  "Pressure Cycles",
  "Additional Forces and Moments",
  "UT readings",
  "Buried Details",
  "Extra Layers",
  "Repair Kit Sizes",
  "Notes",
] as const;

export const getCompositeJobInfoBlocks = ({
  job,
  additionalNotesData,
  repairSystemOptions,
  surfacePreparationOptions,
  allKits,
  componentMaterialLabelById,
}: {
  job: Job;
  additionalNotesData?: AdditionalNotesLookup;
  repairSystemOptions: Array<{ value: number; title: string }>;
  surfacePreparationOptions: Array<{ value: number; title: string }>;
  allKits: Array<{ value: number; title: string }>;
  componentMaterialLabelById?: Record<number, string>;
}) =>
  compositeCardTitles.reduce((acc: any, title) => {
    const newBlock: any = {};
    if (title === "Job Details") {
      newBlock.content = {
        "Job Name": job?.job_name ?? "N/A",
        "Job Number": job?.job_number ?? "N/A",
        "Plant Name": job?.plant_name ?? "N/A",
        "Select service": job?.service_name ?? "N/A",
        "Repair system": repairSystemOptions?.find((r) => r.value === job?.repair_system)?.title ?? "N/A",
        "Surface preparation":
          surfacePreparationOptions?.find((r) => r.value === job?.surface_preparation)?.title ?? "N/A",
        Branch: job?.branch || "N/A",
      };
    } else if (title === "Additional Job Details") {
      newBlock.content = {
        Unit: job?.unit || "N/A",
        "Line id:": job?.line_id ?? "N/A",
      };
    } else if (title === "Contact Information") {
      newBlock.content = {
        "Contact Name": job?.contact_info_name || "N/A",
        "Phone number": job?.contact_info_phone || "N/A",
        Email: job?.contact_info_email || "N/A",
      };
    } else if (title === "Design Conditions") {
      newBlock.content = {
        "Design pressure": job?.design_pressure ?? "N/A",
        "Design temperature": job?.design_temperature
          ? `${job.design_temperature} (${job.unit_for_design_temperature})`
          : "N/A",
      };
    } else if (title === "Operating Conditions") {
      newBlock.content = {
        "Operating Pressure": job?.operating_pressure ?? "N/A",
        "Operating Temp Temperature": job?.operating_temp_temperature
          ? `${job.operating_temp_temperature} (${job.unit_for_operating_temp_temperature})`
          : "N/A",
        "Installation Temperature": job?.installation_temperature
          ? `${job.installation_temperature} (${job.unit_for_installation_temperature})`
          : "N/A",
      };
    } else if (title === "Component Details") {
      const data = job?.components_data
        ?.map((d, index) => {
          const componentNumber = index + 1;
          return {
            [`Component ${componentNumber} nps`]: d.component_nps ?? "N/A",
            [`Component ${componentNumber} diameter`]: d.component_diameter ?? "N/A",
            [`Component ${componentNumber} unit for diameter`]: d.unit_for_component_diameter ?? "N/A",
            [`Component ${componentNumber} schedule`]: d.component_schedule ?? "N/A",
            [`Component ${componentNumber} thickness`]: d.component_thickness ?? "N/A",
            [`Component ${componentNumber} unit for thickness`]: d.unit_for_component_thickness ?? "N/A",
            [`Component ${componentNumber} is flanges`]: "is_flanges" in d ? ((d as any).is_flanges ?? "N/A") : "N/A",
          };
        })
        .reduce((acc: Record<string, any>[], el) => {
          return Object.assign(acc, el);
        }, []);

      newBlock.content = {
        "Component Material": formatMaterialLabel(job?.component_material, componentMaterialLabelById),
        "Component options": job?.component_options?.join(", "),
        ...data,
      };
    } else if (title === "Checks") {
      newBlock.content = {
        "Do you have UT reading info?": booleanToYesNo(job?.do_you_have_ut_reading_info),
        "Calculate at worst case": booleanToYesNo(job?.calculate_at_worst_case),
        "Is UT reading less than 0.04?": booleanToYesNo(job?.is_ut_reading_less_than_04),
        "Per code line leaking, do you know diameter?": booleanToYesNo(job?.per_code_line_leaking_do_you_know_diameter),
        "Component leaking": booleanToYesNo(job?.component_leaking),
        "Is pinhole leak?": booleanToYesNo(job?.is_pinhole_leak),
        "Component patched or repaired?": booleanToYesNo(job?.component_patched_or_repaired),
        "Is component shut down during repair?": booleanToYesNo(job?.is_component_shutdown_during_repair),
        "Add extra layers?": booleanToYesNo(job?.add_extraLayers),
        "Is component buried?": booleanToYesNo(job?.is_component_buried),
        "Design axial loads": booleanToYesNo(job?.design_axial_loads),
        "Is component straight line?": booleanToYesNo(job?.is_component_straight_line),
        "Do you want to take into account any additional forces and moments?": booleanToYesNo(
          job?.do_you_want_to_design_for_external_loads_or_vacuum
        ),
        "Do you want to design for external loads or vacuum?": booleanToYesNo(
          job?.do_you_want_to_design_for_external_loads_or_vacuum
        ),
        "Do you want to design for constant pressure cycles?": booleanToYesNo(
          job?.do_you_want_to_design_for_constant_pressure_cycles
        ),
        "Are the expected cycles more than 7,000 over design life?": booleanToYesNo(
          job?.are_the_expected_cycles_more_than_7000_over_design_life
        ),
        "Do you want to design for continuous long-term strains?": booleanToYesNo(
          job?.do_you_want_to_design_for_continous_long_term_strains
        ),
        "Do you want to completely exclude the component's strength for design?": booleanToYesNo(
          job?.do_you_want_to_completely_exclude_the_components_strength_for_design
        ),
      };
    } else if (title === "Slot Details") {
      newBlock.content = {
        "Slot width in Circumferential Direction": job?.slot_diameter ?? "N/A",
      };
    } else if (title === "Defect Details") {
      const block = job?.defects
        ?.map((d, index) => {
          const componentNumber = index + 1;
          return {
            [`Component ${componentNumber} what is the total defect length?`]: d.defect_length_total ?? "N/A",
            [`Component ${componentNumber} Unit for total`]: d.unit_for_defect_length ?? "N/A",
            [`Component ${componentNumber} How much length do you wish to wrap?`]: d.defect_length_wrap ?? "N?A",
            [`Component ${componentNumber} Unit for wrap`]: d.unit_for_defect_length_wrap ?? "N/A",
          };
        })
        .reduce((acc: Record<string, any>[], el) => {
          return Object.assign(acc, el);
        }, []);
      newBlock.content = {
        "Repair Options": job?.repair_length_options?.join(", ") ?? "N/A",
        ...block,
      };
    } else if (title === "Repair Kit Sizes") {
      newBlock.content =
        job?.repair_length_repair_kit_sizes.reduce((acc: any, n, i) => {
          acc[`kit ${i + 1}`] = allKits.find(({ value }) => value === n)?.title;
          return acc;
        }, {}) ?? {};
    } else if (title === "Pinhole Details") {
      newBlock.content = {
        "pinhole diameter": job?.pinhole_diameter ?? "N/A",
      };
    } else if (title === "Buried Details") {
      newBlock.content = {
        "buried depth": job?.buried_depth ? `${job.buried_depth} (${job.unit_for_buried_check})` : "N/A",
      };
    } else if (title === "Extra Layers") {
      newBlock.content = {
        "Extra layers": job?.extra_layers ?? "N/A",
      };
    } else if (title === "Diameter Encompassing Entire thin Defect Details") {
      newBlock.content = {
        "pinhole 2": job?.pinhole_2 ?? "N/A",
      };
    } else if (title === "Pressure Cycles") {
      newBlock.content = {
        "number of cycles": job?.number_of_cycles ?? "N/A",
        "min cycle pressure": job?.min_cycle_pressure ?? "N/A",
        "max cycle pressure": job?.max_cycle_pressure ?? "N/A",
      };
    } else if (title === "UT readings") {
      newBlock.content = {
        "ut readings": job?.ut_readings ?? "N/A",
      };
    } else if (title === "Additional Forces and Moments") {
      newBlock.content = {
        "applied shared load": job?.applied_sheared_load ?? "N/A",
        "applied axial load": job?.applied_axial_load ?? "N/A",
        "applied tortional moment": job?.applied_tortional_moment ?? "N/A",
        "applied axial moment": job?.applied_axial_moment ?? "N/A",
      };
    } else if (title === "Notes") {
      const notes = additionalNotesData?.notes.filter(({ id }) => job?.additional_notes.includes(id));
      newBlock.content = {
        "Additional notes": formatAdditionalNotesFromList(notes),
      };
    }
    newBlock.title = title;
    return [...acc, newBlock];
  }, []);
